import { Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  EmailContentData,
  EmailPreviewHydrationData,
  PreviewContextSelectorModule,
  PreviewRenderingService,
} from '@galaxy/email-ui/email-builder';
import { WhitelabelService } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SubscriptionList } from '@vendasta/rx-utils';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { SelectedPreviewContext } from '@galaxy/email-ui/email-builder/src/components/preview-context-selector/preview-context-selector.component';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { EmailTemplateService, SenderType } from '@vendasta/campaigns';
import { TranslateModule } from '@ngx-translate/core';

export interface SendPreviewDialogData {
  business: SelectedPreviewContext;
  subjectLine: string;
  emailHtmlContent: string;
  partnerID: string;
  marketID: string;
  locale?: string;
}

@Component({
  templateUrl: './send-preview-dialog.component.html',
  imports: [
    FormsModule,
    GalaxyWrapModule,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatFormField,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    PreviewContextSelectorModule,
    TranslateModule,
  ],
})
export class SendPreviewDialogComponent {
  public form: UntypedFormGroup;
  hydrationData: EmailPreviewHydrationData;

  private subs = SubscriptionList.new();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SendPreviewDialogData,
    public dialogRef: MatDialogRef<SendPreviewDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private emailPreviewService: PreviewRenderingService,
    private readonly campaignsService: EmailTemplateService,
    private readonly alerts: SnackbarService,
    private whitelabelService: WhitelabelService,
    @Inject('USER_ID') private userId$: Observable<string>,
    private readonly translate: TranslateService,
  ) {
    this.hydrationData = {
      business: data.business,
      userID: undefined, // Will be set from subscription below
      contactID: 'default',
      partnerID: this.data.partnerID,
      marketID: this.data.marketID,
      businessID: data.business.accountGroupId,
    };
    this.subs.add(this.userId$, (uid) => {
      this.hydrationData.userID = uid;
      this.emailPreviewService.setPreviewHydrationParams(this.hydrationData);
    });
    this.emailPreviewService.setLocale(data.locale);
    this.userId$.subscribe();
    this.form = this.formBuilder.group({
      sendTo: formBuilder.control('myself'),
    });
  }

  sendPreviewEmail(): void {
    const target = this.form.get('sendTo').value;
    if (target === 'myself') {
      this.userId$
        .pipe(
          switchMap((userID) => {
            const hydrationData = {
              subjectLine: this.data.subjectLine,
            } as EmailContentData;

            return this.hydrateTemplate(userID, this.hydrationData?.contactID, hydrationData);
          }),
          switchMap((result) => {
            return this.getSenderInfo(result.userID, result.content, result.subject);
          }),
          take(1),
          switchMap((result) => {
            return this.campaignsService.sendPreview(
              {
                id: result.businessID,
                type: SenderType.SENDER_TYPE_BUSINESS,
              },
              result.content,
              result.subject || 'Preview for unnamed template',
              result.userID,
            );
          }),
        )
        .subscribe(
          () => this.handleSendSuccess(),
          (error) => this.handleSendError(error),
        );
    }
  }

  private hydrateTemplate(
    userID: string,
    contactID: string,
    hydrationData: EmailContentData,
  ): Observable<{
    userID: string;
    partnerID: string;
    content: string;
    subject: string;
    namespace: string;
    contactID: string;
    businessID: string;
  }> {
    const hydratedContent = this.emailPreviewService.hydrateTemplate(hydrationData, this.data.emailHtmlContent);
    const hydratedSubject = this.emailPreviewService.hydrateSubject(hydrationData, this.data.subjectLine);
    return combineLatest([hydratedContent, hydratedSubject]).pipe(
      map(([content, subject]) => ({
        userID: userID,
        partnerID: this.data.partnerID,
        namespace: this.data.partnerID,
        contactID: contactID,
        content: content,
        subject: subject,
        businessID: this.data.business.accountGroupId,
      })),
    );
  }

  private getSenderInfo(
    userID: string,
    content: string,
    subject: string,
  ): Observable<{
    userID: string;
    partnerID: string;
    content: string;
    subject: string;
    senderName: string;
    senderEmail: string;
    businessID: string;
  }> {
    return this.whitelabelService.getConfiguration(this.data.partnerID, null).pipe(
      map((configuration) => {
        // The defaults here are written to mirror AA/src/app/handlers/marketing_automation/templates.py:126
        const senderName = configuration?.emailConfiguration?.senderName || 'Campaign Email Preview';
        const senderEmail = configuration?.emailConfiguration?.senderEmail || 'noreply@vendasta.com';
        return {
          userID: userID,
          partnerID: this.data.partnerID,
          content: content,
          subject: subject,
          senderName: senderName,
          senderEmail: senderEmail,
          businessID: this.data.business.accountGroupId,
        };
      }),
    );
  }

  handleSendSuccess(): void {
    this.alerts.openSuccessSnack(this.translate.instant('CAMPAIGN_PREVIEW.SEND_SUCCESS'));
    this.dialogRef.close();
  }

  handleSendError(error: Error): void {
    this.alerts.openErrorSnack(this.translate.instant('CAMPAIGN_PREVIEW.SEND_FAILED'));
    console.error(error);
  }

  updateSelectedContact($event: string): void {
    this.hydrationData.contactID = $event;
    this.emailPreviewService.setPreviewHydrationParams(this.hydrationData);
  }
}
